import { useState, useEffect } from "react";
import { useTheme } from "next-themes";
import { Button } from "@/components/ui/button";
import { Sun, Moon, Menu, X } from "lucide-react";

const navLinks = [
  { label: "How it works", href: "#how-it-works" },
  { label: "Events", href: "#events" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
  { label: "For venues", href: "#venues" },
];

export default function Header({ onBookSpot, onJoinWaitlist }) {
  const { theme, setTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 glass-header transition-all duration-300 ${
        scrolled
          ? "bg-background/80 border-b border-border/50 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2" data-testid="logo">
            <span className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
              Cita Rush
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
                data-testid={`nav-${link.href.replace("#", "")}`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-3">
            <button
              onClick={onJoinWaitlist}
              className="hidden sm:inline-flex text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              data-testid="header-women-waitlist-btn"
            >
              Women waitlist (Free)
            </button>
            <Button
              onClick={onBookSpot}
              className="hidden sm:inline-flex rounded-sm px-6 py-2 text-sm font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5"
              data-testid="header-book-spot-btn"
            >
              Book a spot
            </Button>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="theme-toggle-btn"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun className="w-5 h-5" strokeWidth={1.5} /> : <Moon className="w-5 h-5" strokeWidth={1.5} />}
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden bg-background/95 glass-header border-t border-border/50"
          data-testid="mobile-menu"
        >
          <nav className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="text-base font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                {link.label}
              </a>
            ))}
            <div className="flex flex-col gap-3 pt-4 border-t border-border/50">
              <Button
                onClick={() => { onBookSpot(); setMobileMenuOpen(false); }}
                className="rounded-sm"
                data-testid="mobile-book-spot-btn"
              >
                Book a spot
              </Button>
              <Button
                variant="outline"
                onClick={() => { onJoinWaitlist(); setMobileMenuOpen(false); }}
                className="rounded-sm"
                data-testid="mobile-women-waitlist-btn"
              >
                Women waitlist (Free)
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
